import { Component, EventEmitter, OnInit, Output } from '@angular/core';

@Component({
    selector: 'vb-pickup-calendar',
    templateUrl: './pickup-calendar.component.html',
    styleUrls: ['./pickup-calendar.component.scss'],
})
export class PickupCalendarComponent implements OnInit {
    daysOfWeek = [
        { label: 'S', value: 'Sunday' },
        { label: 'M', value: 'Monday' },
        { label: 'T', value: 'Tuesday' },
        { label: 'W', value: 'Wednesday' },
        { label: 'T', value: 'Thursday' },
        { label: 'F', value: 'Friday' },
        { label: 'S', value: 'Saturday' },
    ];
    monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    calendarDays: ({ day: number; selectable: boolean } | null)[][] = [];
    selectedDate = new Date();
    currentMonth: number;
    currentYear: number;
    today: Date;
    @Output() dateSelected = new EventEmitter<Date>();
    @Output() monthChanged = new EventEmitter<{
        start: string;
        end: string;
    }>();
    monthStartDate: string = '';
    monthEndDate: string = '';

    constructor() {
        this.today = new Date();
        this.currentMonth = this.today.getMonth();
        this.currentYear = this.today.getFullYear();
        const tomorrow: Date = new Date(this.today);
        tomorrow.setDate(this.today.getDate() + 1);
        this.selectedDate = tomorrow;
    }

    ngOnInit(): void {
        this.dateSelected.emit(this.selectedDate);
        this.generateCalendar();
        this.updateMonthDateRange();
    }

    generateCalendar(): void {
        const startOfMonth: Date = new Date(
            this.currentYear,
            this.currentMonth,
            1,
        );
        const endOfMonth: Date = new Date(
            this.currentYear,
            this.currentMonth + 1,
            0,
        );

        const startDay: number = startOfMonth.getDay();
        const daysInCurrentMonth: number = endOfMonth.getDate();

        // Previous month's dates
        const prevMonth: number =
            this.currentMonth === 0 ? 11 : this.currentMonth - 1;
        const prevYear: number =
            this.currentMonth === 0 ? this.currentYear - 1 : this.currentYear;
        const daysInPrevMonth: number = new Date(
            prevYear,
            prevMonth + 1,
            0,
        ).getDate();

        // Today's date for comparison
        const today: Date = new Date();
        const isCurrentMonth: boolean =
            this.currentYear === today.getFullYear() &&
            this.currentMonth === today.getMonth();
        const currentDay: number | null = isCurrentMonth
            ? today.getDate()
            : null;

        this.calendarDays = [];
        let week: { day: number; selectable: boolean }[] = [];

        // Add empty slots for days before the 1st of the month
        for (let i: number = 0; i < startDay; i++) {
            week.push({ day: 0, selectable: false });
        }

        // Add days for the current month
        for (let day: number = 1; day <= daysInCurrentMonth; day++) {
            // Make days before or equal to today unselectable in the current month
            const isSelectable: boolean = currentDay ? day > currentDay : true;
            week.push({ day, selectable: isSelectable });

            // Once a week (7 days) is filled, push to calendarDays
            if (week.length === 7) {
                this.calendarDays.push(week);
                week = [];
            }
        }

        // Push any remaining days without filling in next month's dates
        if (week.length > 0) {
            this.calendarDays.push(week);
        }
    }

    previousMonth(): void {
        if (this.currentMonth > 0) {
            this.currentMonth--;
        } else {
            this.currentYear--;
            this.currentMonth = 11;
        }
        this.generateCalendar();
        this.updateMonthDateRange();
    }

    nextMonth(): void {
        if (this.currentMonth < 11) {
            this.currentMonth++;
        } else {
            this.currentYear++;
            this.currentMonth = 0;
        }
        this.generateCalendar();
        this.updateMonthDateRange();
    }

    selectDate(day: number): void {
        if (day) {
            const selected: Date = new Date(
                this.currentYear,
                this.currentMonth,
                day,
            );
            if (selected >= this.stripTime(this.today)) {
                this.selectedDate = selected;
                this.dateSelected.emit(this.selectedDate);
            }
        }
    }

    isSelected(day: number): boolean {
        return (
            day === this.selectedDate.getDate() &&
            this.currentMonth === this.selectedDate.getMonth() &&
            this.currentYear === this.selectedDate.getFullYear()
        );
    }

    canNavigatePrevious(): boolean {
        return !(
            this.currentYear === this.today.getFullYear() &&
            this.currentMonth === this.today.getMonth()
        );
    }

    private stripTime(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private formatDate(date: Date): string {
        const year: number = date.getFullYear();
        const month: string = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const day: string = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    updateMonthDateRange(): void {
        const startOfMonth: Date = new Date(
            this.currentYear,
            this.currentMonth,
            1,
        );
        const endOfMonth: Date = new Date(
            this.currentYear,
            this.currentMonth + 1,
            0,
        );

        this.monthStartDate = this.formatDate(startOfMonth);
        this.monthEndDate = this.formatDate(endOfMonth);

        this.monthChanged.emit({
            start: this.monthStartDate,
            end: this.monthEndDate,
        });
    }
}
